import { AxiosResponse } from 'axios';
import React, { createRef, useEffect, useState } from 'react';
import { TFunction, withTranslation } from 'react-i18next';
import { DateObject } from "react-multi-date-picker";
import { OptionMapperHelper } from '../../helpers/OptionMapperHelper';
import { IIssuer } from "../../models/IIssuer";
import { ITransaction, ITransactionWebModelRequest } from "../../requests/ITransactionWebModelRequest";
import { TransactionService } from '../../services/TransactionService';
import { UtilService } from "../../services/UtilService";
import { WebCache } from "../../services/WebCache";
import ErrorsView from '../ErrorsView';
import TerminalLocation from "../TerminalLocation";
import DateField from '../controls/fields/dateField';
import SelectField from '../controls/fields/selectField';
import TextField from '../controls/fields/textField';
import Page from '../containers/page'
import FormFilter from '../containers/form-filter';
import { DateHelper } from '../../helpers/DateHelper';
import NavigationTabLinksForTransactions from './NavigationTabLinksForTransactions';
import "./TransactionReport.css";
import { ColumnDefinition, currencyColumnDefinition, numericColumnDefinition, textColumnDefinition } from '../table/ColumnDefinition';
import CustomTable, { TableModeEnum } from '../table/CustomTable';
import { toast } from 'react-toastify';
import Pagination from '../Pagination';
import { CurrencyHelper } from '../../helpers/CurrencyHelper';

interface IProps {
    t: TFunction
}
interface IFilters {
    fromDate: DateObject,
    toDate: DateObject,
    operationTypeFilter: string,
    transactionStateFilter: string,
    terminalFilter: string,
    merchantFilter: string,
    initQuestionFilter: string,
    groupFilter: string,
    transactionIdFilter: string
    currencySelected: string | null,
    departmentFilter: string,
    locationFilter: string,
    branchFilter: string,
    taxPayerFilter: string[]
}

const pageSize = 20;
const pageQuantity = 3
const defaultPages = [1, 2, 3]
const T_PREFIX: string = 'pages.reportTransactions.';

const CurrentTransactionReport: React.FC<IProps> = ({ t }: IProps) => {
    const allTransactionStatesFilter = localStorage.getItem("_transactionStatesCode") ? JSON.parse(localStorage.getItem("_transactionStatesCode") as string) : [];
    const allOperationTypesFilters = localStorage.getItem("_transactionTypesCode") ? JSON.parse(localStorage.getItem("_transactionTypesCode") as string) : [];


    let [dateFrom, dateTo] = DateHelper.buildDateRange();
    const [filters, setFilters] = useState<IFilters>({ fromDate: dateFrom, toDate: dateTo, operationTypeFilter: "", transactionStateFilter: "", terminalFilter: "", initQuestionFilter: "", groupFilter: "", merchantFilter: "", transactionIdFilter: "", departmentFilter: "", locationFilter: "", branchFilter: "", taxPayerFilter: [], currencySelected: "" })
    const [shouldFetch, setShouldFetch] = useState(false);
    const [transactions, setTransacations] = useState <ITransaction[]>([])
    const [errors,setErrors] = useState <string[]>([])
    const [activePage,setActivePage]= useState<number>(1)
    const [mainPage,setMainPage]= useState<number>(0)
    const [pages,setPages] = useState <number[]>(defaultPages)
    const [currencies,setCurrencies] = useState<{ label: string; value: string }[]>(UtilService.getCurrencyOptions());
    const [taxPayerName,setTaxPayerName] = useState <string>("")
    const [departmentName,setDepartmentName] = useState <string>("")
    const [locationName,setLocationName] = useState <string>("")
    const [branchName,setBranchName] = useState <string>("")
    const [maxDayRange,setMaxDayRange] = useState<number>(15)
    const divColumnSize: string = UtilService.isMobilePlatform() ? "col-md-6" : "col-sm-12 col-md-4 col-lg-3";


    const currentTransactions: ColumnDefinition[] = [
        textColumnDefinition({ key: 'id', label: t(`${T_PREFIX}transactionsColumn.id`), header: true }),
        textColumnDefinition({
            key: 'authDateTime', label: t(`${T_PREFIX}transactionsColumn.authDateTime`), header: true, mapValue: (rowObject: ITransaction) => {
                const authDate = rowObject.authDateTime
                return UtilService.getDDMMyyHHmmssFormatFromString(authDate.toString())
            }
        }),
        textColumnDefinition({
            key: 'terminal', label: t(`${T_PREFIX}transactionsColumn.terminal`), header: true, mapValue: (rowObject: ITransaction) => {
                const terminal = rowObject.terminal
                if (terminal.toString().includes("B24_")) {
                    return t(terminal).substring(4)
                } else {
                    return t(terminal)
                }
            }
        }),
        textColumnDefinition({
            key: 'transactionType', label: t(`${T_PREFIX}transactionsColumn.transactionType`), header: true, mapValue: (rowObjects: ITransaction) => {
                const transactionType = rowObjects.transactionType
                return t("transactionType" + "-" + transactionType)
            }
        }),
        numericColumnDefinition({
            key: 'state', label: t(`${T_PREFIX}transactionsColumn.state`), header: true, mapValue: (rowObjects: ITransaction) => {
                const state = rowObjects.state
                return t("state" + "-" + state)
            }
        }),
        textColumnDefinition({
            key: 'currency', label: t(`${T_PREFIX}transactionsColumn.currency`), header: true, mapValue: (rowObject: ITransaction) => {
                const currency = rowObject.currency
                return t("currency" + "-" + currency)
            }
        }),
        currencyColumnDefinition({
            key: 'totalAmountForReport', label: t(`${T_PREFIX}transactionsColumn.totalAmountForReport`), columnDefinitionKey: 'currency', header: true, mapValue: (rowObject: ITransaction) => {
                const totalAmountForReport = rowObject.totalAmountForReport
                return <div>{Intl.NumberFormat('es-UY', { style: 'currency', currency: rowObject.currency == "840" ? "USD" : "UYU" }).format(Number(totalAmountForReport))}</div>
            }
        }),
        textColumnDefinition({ key: 'merchant', label: t(`${T_PREFIX}transactionsColumn.merchant`), header: true }),
        textColumnDefinition({ key: 'authTerminal', label: t(`${T_PREFIX}transactionsColumn.authTerminal`), header: true }),
        currencyColumnDefinition({ key: 'tipAmountForReport', label: t(`${T_PREFIX}transactionsColumn.tipAmountForReport`), columnDefinitionKey: 'currency', header: true }),
        currencyColumnDefinition({
            key: 'taxAmountForReport', label: t(`${T_PREFIX}transactionsColumn.taxAmountForReport`), columnDefinitionKey: 'currency', header: true, mapValue: (rowObject: ITransaction) => {
                const taxAmountForReport = rowObject.taxAmountForReport
                return Intl.NumberFormat('es-UY', { style: 'currency', currency: rowObject.currency == "840" ? "USD" : "UYU" }).format(Number(taxAmountForReport))
            }
        }),
        textColumnDefinition({ key: 'quotas', label: t(`${T_PREFIX}transactionsColumn.quotas`), header: true }),
        textColumnDefinition({
            key: 'billNumber', label: t(`${T_PREFIX}transactionsColumn.billNumber`), header: true, mapValue: (rowObject: ITransaction) => {
                const billNumber = rowObject.billNumber
                return billNumber.replace(/^0+/, '')
            }
        }),
        textColumnDefinition({ key: 'additionalData', label: t(`${T_PREFIX}transactionsColumn.additionalData`), header: true }),
        textColumnDefinition({ key: 'expirationDate', label: t(`${T_PREFIX}transactionsColumn.expirationDate`), header: true }),
        textColumnDefinition({ key: 'originalDate', label: t(`${T_PREFIX}transactionsColumn.originalDate`), header: true }),
        currencyColumnDefinition({ key: 'billAmountForReport', label: t(`${T_PREFIX}transactionsColumn.billAmountForReport`), columnDefinitionKey: 'currency', header: true }),
        currencyColumnDefinition({ key: 'taxableAmountForReport', label: t(`${T_PREFIX}transactionsColumn.taxableAmountForReport`), columnDefinitionKey: 'currency', header: true }),
        textColumnDefinition({ key: 'initQuestion', label: t(`${T_PREFIX}transactionsColumn.initQuestion`), header: true }),
        textColumnDefinition({ key: 'terminalGroup', label: t(`${T_PREFIX}transactionsColumn.terminalGroup`), header: true }),
        textColumnDefinition({ key: 'address', label: t(`${T_PREFIX}transactionsColumn.address`), header: true }),
        textColumnDefinition({ key: 'location', label: t(`${T_PREFIX}transactionsColumn.location`), header: true }),
        textColumnDefinition({ key: 'department', label: WebCache.getCurrentUser()?.countryId == 2 ? t(`${T_PREFIX}transactionsColumn.departamentUy`) : t(`${T_PREFIX}transactionsColumn.departamentArg`), header: true }),
        textColumnDefinition({ key: 'wholesalerName', label: t(`${T_PREFIX}transactionsColumn.wholesalerName`), header: true }),
        textColumnDefinition({ key: 'specialData', label: t(`${T_PREFIX}transactionsColumn.specialData`), header: true }),
        textColumnDefinition({ key: 'userId', label: t(`${T_PREFIX}transactionsColumn.userId`), header: true })
    ]

    const getTransactionRequest = (all: boolean) => {

        let from_year: number;
        let from_month: number;
        let from_day: number;

        let to_year: number;
        let to_month: number;
        let to_day: number;

        from_year = filters.fromDate.year;
        from_month = filters.fromDate.month.number;
        from_day = filters.fromDate.day;

        to_year = filters.toDate.year;
        to_month = filters.toDate.month.number;
        to_day = filters.toDate.day;

        const getIdOfFilter = (value: string) => {// Obtiene el id del filtro de transaccion seleccionado, este separado por "-"
            return value.split("-")[1];
        };

        const tranWebModel: ITransactionWebModelRequest = {
            From: new Date(from_year, from_month - 1,
                from_day,
                filters.fromDate.hour - 3,
                filters.fromDate.minute).toJSON(),
            To: new Date(to_year,
                to_month - 1,
                to_day,
                filters.toDate.hour - 3,
                filters.toDate.minute).toJSON(),

            Terminals: (filters.terminalFilter == null ||
                filters.terminalFilter == "") ? [] : [filters.terminalFilter], //todo falta filtrar las terminales del usuario

            // @ts-ignore
            TransactionState: (filters.transactionStateFilter == null || filters.transactionStateFilter == "") ? [] : filters.transactionStateFilter.split("-").map(Number),
            OperationType: (filters.operationTypeFilter == null) ? "" : getIdOfFilter(filters.operationTypeFilter),
            TerminalGroup: (filters.groupFilter == null || filters.groupFilter == "") ? "" : filters.groupFilter,
            InitQuestion: (filters.initQuestionFilter == null || filters.initQuestionFilter == "") ? "" : filters.initQuestionFilter,
            MerchantCodes: (filters.merchantFilter == null || filters.merchantFilter == "") ? [] : [filters.merchantFilter],
            Department: (filters.departmentFilter == null || filters.departmentFilter == "0000") ? "" : filters.departmentFilter,
            Location: (filters.locationFilter == null || filters.locationFilter == "0000") ? "" : filters.locationFilter,
            PayerTaxIds: filters.taxPayerFilter.includes("all") ? [] : filters.taxPayerFilter.filter(x => x.length > 0),
            Branch: (filters.branchFilter == null || filters.branchFilter == "0000") ? "" : filters.branchFilter,
            TransactionId: (filters.transactionIdFilter == null || filters.transactionIdFilter == "") ? "" : filters.transactionIdFilter,
            Skip: all ? 0 : (mainPage * pageSize * pageQuantity),
            Take: all ? 0 : ((pageSize * pageQuantity) + 1),
            Currency: filters.currencySelected,
        };
        return tranWebModel;
    }

    const getTicket = (tranId: string, transactionType: string) => {
        TransactionService.showTicket(tranId, transactionType)
    }

    const getTransactions = () => {

        let dateFrom = DateHelper.dateObjectToDate(filters.fromDate);
        let dateTo = DateHelper.dateObjectToDate(filters.toDate);
        let diffDays = Math.round((dateTo.getTime() - dateFrom.getTime()) / (1000 * 3600 * 24));
        if (diffDays > maxDayRange) {
            toast.error(`La busqueda no puede superar los ${maxDayRange} días`);
            return;
        }

        TransactionService.getCurrentTransactions(getTransactionRequest(false)).then((response: AxiosResponse<ITransaction[]>) => {
            if (response == null || response.data == null || response.data.length == 0) {
                setTransacations([])
                setErrors([])
            }
            setTransacations(response.data)
            setErrors([])
        }).catch(error => {
            setErrors(error.response.data)
            setTransacations([])
            console.log('error:', error);
        }
        );
    }

    const getFromBuscar = () => {
        setActivePage(1)
        setPages(defaultPages)
        setMainPage(0)
        getTransactions()
    }

    const clearFilters = () => {
        setFilters({
            ...filters,
            fromDate: dateFrom,
            toDate: dateTo,
            operationTypeFilter: "",
            transactionStateFilter: "",
            terminalFilter: "",
            merchantFilter: "",
            initQuestionFilter: "",
            groupFilter: "",
            transactionIdFilter: "",
            currencySelected: "",
            taxPayerFilter: [],
            branchFilter: "",
            departmentFilter: "",
            locationFilter: "",
        });
        setShouldFetch(true); // Marca que hay que hacer fetch
    }
    //obtiene valores predeterminados cuando se hace clearFilters
    useEffect(() => {
        if (shouldFetch) {
            getFromBuscar();
            setShouldFetch(false); // Resetea la bandera
        }
    }, [filters, shouldFetch]);

    return (
        <Page>
            <>
                <h2>{t(`sales`)}</h2>
                <div className='row'>
                    <div className='col-lg-12'>
                        <ErrorsView errors={errors} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-12">
                        <NavigationTabLinksForTransactions></NavigationTabLinksForTransactions>
                        <FormFilter
                            onSubmit={() => getFromBuscar()}
                            model={[filters, setFilters]}
                            hidden={UtilService.isMobilePlatform() ? true : false}

                            clear={() => clearFilters()}
                        >
                            <>
                                <div className="row">
                                    <div className={divColumnSize}>
                                        <DateField attr='fromDate' label='authDateTimeFrom' ></DateField>
                                    </div>
                                    <div className={divColumnSize}>
                                        <DateField attr='toDate' label='authDateTimeTo' ></DateField>
                                    </div>
                                    <div className={divColumnSize}>
                                        <SelectField attr='operationTypeFilter' label='transactionType' options={new OptionMapperHelper(t).buildTransactionSelectOptions(allOperationTypesFilters, false)} ></SelectField>
                                    </div>
                                    <div className={divColumnSize}>
                                        <SelectField attr='transactionStateFilter' label='state' options={new OptionMapperHelper(t).buildTransactionSelectOptions(allTransactionStatesFilter, false, true)} ></SelectField>
                                    </div>
                                    <div className={divColumnSize}>
                                        <TextField attr='terminalFilter' label='terminal' ></TextField>
                                    </div>
                                    <div className={divColumnSize}>
                                        <TextField attr='merchantFilter' label='merchant' ></TextField>
                                    </div>
                                    <div className={divColumnSize}>
                                        <TextField attr='initQuestionFilter' label='initQuestion' ></TextField>
                                    </div>
                                    <div className={divColumnSize}>
                                        <TextField attr='groupFilter' label='terminalGroup' ></TextField>
                                    </div>
                                    <div className={divColumnSize}>
                                        <TextField attr='transactionIdFilter' label='id' ></TextField>
                                    </div>
                                    <TerminalLocation
                                        changeFilter={(taxPayerFilter, branchFilter, departmentFilter, locationFilter, departmentName, locationName, branchName, clientDocument) => {
                                            setFilters({
                                                ...filters,
                                                taxPayerFilter: taxPayerFilter,
                                                branchFilter: branchFilter,
                                                departmentFilter: departmentFilter,
                                                locationFilter: locationFilter,
                                            });
                                            setTaxPayerName(clientDocument);
                                            setDepartmentName(departmentName);
                                            setLocationName(locationName);
                                            setBranchName(branchName);
                                        }}
                                        filter={{
                                            taxPayerFilter: filters.taxPayerFilter,
                                            branchFilter: filters.branchFilter,
                                            departmentFilter: filters.departmentFilter,
                                            locationFilter: filters.locationFilter,
                                        }}
                                    />         
                                    <div className={divColumnSize}>
                                        <SelectField attr='currencySelected' label='currency' options={currencies} ></SelectField>
                                    </div>
                                </div>
                            </>
                        </FormFilter>
                        {UtilService.isMobilePlatform() ?
                            <div>
                                <div className="container">
                                    <ul className="notification">
                                        {
                                            transactions.slice(pages.indexOf(activePage) * pageSize, (pages.indexOf(activePage) * pageSize) + pageSize).map((transaction, Tindex) => {
                                                return (
                                                    <li>
                                                        <div className="notification-time-date mb-2 d-block d-md-none">
                                                            <span className="date">{transaction.authDateTime}</span>
                                                        </div>
                                                        <div className={"settlementMobile notification-body transactionType-" + transaction.transactionType}>
                                                            <div className="media mt-0">
                                                                <div className="main-avatar avatar-md online" >
                                                                    {<img alt="avatar" className="br-7 transaction-image" src={UtilService.getImageTag(t("issuer-" + transaction.issuer).toUpperCase())}></img>
                                                                    }
                                                                </div>
                                                                <div className="media-body ms-3 d-flex">
                                                                    <div className="" >
                                                                        <span className={transaction.transactionType == "REV" || transaction.transactionType == "LP" || transaction.transactionType == "A" || transaction.transactionType == "D" ? "text-danger amount-label" : "amount-label"}>{Intl.NumberFormat('es-UY', {
                                                                            style: 'currency',
                                                                            currency: CurrencyHelper.getCurrencySymbol(transaction.currency)
                                                                        }).format(transaction.totalAmountForReport)}</span>
                                                                        <p className="mb-0 fs-13 text-muted">{t("transactionType-" + transaction.transactionType)}</p>
                                                                        <p className="mb-0 fs-13 text-muted">{transaction.cardNumber}</p>
                                                                        <p className={"mb-0 fs-11 " + (transaction.responseCode == "00" ||
                                                                            transaction.responseCode == "08" ||
                                                                            transaction.responseCode == "11" ||
                                                                            transaction.responseCode == "85" ||
                                                                            transaction.responseCode == "LV" ||
                                                                            transaction.responseCode == "OF" ? "response-code-success" : "response-code-error")}>{t("responseCode-" + transaction.responseCode)}</p>
                                                                    </div>
                                                                </div>
                                                                <div style={{ width: "34px", height: "30px", marginLeft: "10px" }}>
                                                                    <a onClick={() => { getTicket(transaction.id, transaction.transactionType) }}>
                                                                        <i className="fa-sharp fa-solid fa-print text-primary" aria-hidden="true"></i>
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </li>
                                                )
                                            })}
                                    </ul>
                                </div>
                                <div>
                                    <Pagination
                                        pageSize={pageSize}
                                        pageQuantity={pageQuantity}
                                        modifyMainPage={(value: number, newPages: Array<number>) => {
                                            setMainPage(mainPage + value);
                                            setPages(newPages);
                                            getTransactions();
                                        }}
                                        changePage={(page: number) => setActivePage(page)}
                                        dataL={transactions.length}
                                        mainPage={mainPage}
                                    ></Pagination>
                                </div>
                            </div>
                            :
                            <div className="col-md-12 settlementTable">
                                <CustomTable
                                    rowObjects={transactions}
                                    columnDefinitions={currentTransactions}
                                    config={{
                                        pageSize: pageSize,
                                        mode: TableModeEnum.GRID,
                                        paginator: {
                                            quantity: pageQuantity,
                                            mainPage: mainPage,
                                            modifyMainPage: (value: number) => {
                                                setMainPage(mainPage + value);
                                                getTransactions();
                                            },
                                            pageRequest: {
                                                Skip: 0,
                                                Take: 0
                                            }
                                        },
                                    }}
                                ></CustomTable>
                            </div>}
                    </div>
                </div>
            </>
        </Page>
    )
};

export default withTranslation()(CurrentTransactionReport);